import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import Login from './Login';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || (import.meta && import.meta.env && import.meta.env.REACT_APP_BACKEND_URL) || '';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
};

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Calculator language="en" />} />
        <Route path="/en" element={<Calculator language="en" />} />
        <Route path="/ar" element={<Calculator language="ar" />} />
        <Route path="/staff-console" element={<AdminPage />} />
      </Routes>
    </Router>
  );
}

function Calculator({ language }) {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [notes, setNotes] = useState([]);

  // Detect if running in iframe and add class
  useEffect(() => {
    if (window.self !== window.top) {
      document.body.classList.add('in-iframe');
    }
  }, []);

  // Function to scroll to calculator
  const scrollToCalculator = () => {
    const calculatorElement = document.getElementById('nutrition-calculator');
    if (calculatorElement) {
      calculatorElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Function to remove all items from basket
  const removeAllItems = () => {
    setSelectedItems([]);
  };

  // Function to scroll carousel - same behavior for all languages
  // We force LTR direction on carousel container via CSS, so scroll works consistently
  const scrollCarousel = (container, amount) => {
    if (container) {
      container.scrollBy({ left: amount, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchItems();
    fetchNotes();
  }, []);

  // Saudi Arabic translations
  const translations = {
    en: {
      title: 'Herfy Nutrition Calculator',
      subtitle: 'Herfy Daily Values (DV) are based on a 2,000 calorie diet',
      selectItems: 'Herfy Menu Items',
      yourSelection: 'Your Herfy Selection',
      items: 'items',
      nutritionFacts: 'Herfy Nutrition Facts',
      nutritionCalculator: 'Herfy Nutrition Calculator',
      energy: 'Energy',
      totalFat: 'Total fat',
      carbs: 'Carbs',
      protein: 'Protein',
      serving: 'Serving',
      saturatedFats: 'Saturated Fats',
      transFats: 'Trans Fats',
      cholesterol: 'Cholesterol',
      sodium: 'Sodium',
      dietaryFibers: 'Dietary Fibers',
      totalSugar: 'Total Sugar',
      addedSugar: 'Added Sugar',
      physicalActivity: 'Physical Activity',
      minutes: 'Minutes',
      add: 'Add',
      remove: 'Remove'
    },
    ar: {
      title: 'حاسبة هرفي للقيمة الغذائية',
      subtitle: 'النسب المئوية للقيم اليومية (DV) في هرفي مبنية على نظام غذائي يحتوي على 2,000 سعرة حرارية',
      selectItems: 'أصناف هرفي',
      yourSelection: 'اختيارك من هرفي',
      items: 'صنف',
      nutritionFacts: 'القيمة الغذائية لهرفي',
      nutritionCalculator: 'حاسبة هرفي للقيمة الغذائية',
      energy: 'الطاقة',
      totalFat: 'إجمالي الدهون',
      carbs: 'الكربوهيدرات',
      protein: 'البروتين',
      serving: 'الحصة',
      saturatedFats: 'الدهون المشبعة',
      transFats: 'الدهون المتحولة',
      cholesterol: 'الكوليسترول',
      sodium: 'الصوديوم',
      dietaryFibers: 'الألياف الغذائية',
      totalSugar: 'إجمالي السكر',
      addedSugar: 'السكر المضاف',
      physicalActivity: 'النشاط البدني',
      minutes: 'دقيقة',
      add: 'أضف',
      remove: 'إزالة'
    }
  };

  const t = translations[language];

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/categories`);
      const data = await response.json();
      setCategories(data);
      if (data.length > 0 && !activeCategory) {
        setActiveCategory(data[0].id);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/items`);
      const data = await response.json();
      setItems(data);
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/notes`);
      const data = await response.json();
      setNotes(data);
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  };

  const addItemToBasket = (item) => {
    const isSelected = selectedItems.some(selectedItem => selectedItem.id === item.id);
    if (isSelected) {
      // Remove the item if it's already selected
      setSelectedItems(selectedItems.filter(selectedItem => selectedItem.id !== item.id));
    } else {
      // Add the item if it's not selected
      setSelectedItems([...selectedItems, { ...item, uniqueId: Date.now() + Math.random() }]);
    }
  };

  const removeItemFromBasket = (uniqueId) => {
    setSelectedItems(selectedItems.filter(item => item.uniqueId !== uniqueId));
  };

  const calculateTotalNutrition = () => {
    if (selectedItems.length === 0) {
      return null;
    }

    const total = selectedItems.reduce((acc, item) => {
      const facts = item.nutrition_facts;
      return {
        energy: acc.energy + facts.energy,
        total_fat: acc.total_fat + facts.total_fat,
        carbs: acc.carbs + facts.carbs,
        protein: acc.protein + facts.protein,
        serving: acc.serving + facts.serving,
        saturated_fats: acc.saturated_fats + facts.saturated_fats,
        trans_fats: acc.trans_fats + facts.trans_fats,
        cholesterol: acc.cholesterol + facts.cholesterol,
        sodium: acc.sodium + facts.sodium,
        dietary_fibers: acc.dietary_fibers + facts.dietary_fibers,
        total_sugar: acc.total_sugar + facts.total_sugar,
        added_sugar: acc.added_sugar + facts.added_sugar,
        physical_activity: acc.physical_activity + facts.physical_activity,
      };
    }, {
      energy: 0, total_fat: 0, carbs: 0, protein: 0, serving: 0,
      saturated_fats: 0, trans_fats: 0, cholesterol: 0, sodium: 0,
      dietary_fibers: 0, total_sugar: 0, added_sugar: 0, physical_activity: 0
    });

    return total;
  };

  const filteredItems = items.filter(item => item.category_id === activeCategory);
  const totalNutrition = calculateTotalNutrition();

  return (
    <div className={`App ${language === 'ar' ? 'rtl' : ''}`}>
      {/* Language Toggle - Top Right */}
      {/* Hidden as per user request
      <div className="language-toggle-topbar">
        <Link 
          to="/en" 
          className={`lang-toggle-link ${language === 'en' ? 'active' : ''}`}
        >
          EN
        </Link>
        <span className="lang-divider">|</span>
        <Link 
          to="/ar" 
          className={`lang-toggle-link ${language === 'ar' ? 'active' : ''}`}
        >
          AR
        </Link>
      </div>
      */}

      {/* Calculator View */}
      <div className="calculator-view">
          {/* Hero Section */}
          <div className="hero-section">
            <div className="hero-content">
              <h1 className="hero-title">{t.title}</h1>
              <p className="hero-subtitle">
                {t.subtitle}
              </p>
            </div>
          </div>

          <div className="container">
            {/* Category Tabs - Horizontal Scrollable Carousel */}
            <div className="carousel-container">
              <button 
                className="carousel-arrow carousel-arrow-left"
                onClick={() => {
                  const container = document.querySelector('.category-tabs');
                  scrollCarousel(container, -300);
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              
              <div className="category-tabs">
                {categories.map(category => (
                  <button
                    key={category.id}
                    className={`category-tab ${activeCategory === category.id ? 'active' : ''}`}
                    onClick={() => setActiveCategory(category.id)}
                  >
                    {category.image && (
                      <img src={category.image} alt={category.name} className="category-tab-image" />
                    )}
                    <span>{language === 'ar' && category.arabic_name ? category.arabic_name : category.name}</span>
                  </button>
                ))}
              </div>
              
              <button 
                className="carousel-arrow carousel-arrow-right"
                onClick={() => {
                  const container = document.querySelector('.category-tabs');
                  scrollCarousel(container, 300);
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            {/* Menu Items Grid - Horizontal Scrollable Carousel */}
            <div className="menu-section">
              <div className="menu-section-header">
                <h2 className="section-title">{t.selectItems}</h2>
                {/* Calculator Button - appears when items are selected */}
                {selectedItems.length > 0 && (
                  <button className="calculator-btn" onClick={scrollToCalculator}>
                    <span className="calculator-btn-count">{selectedItems.length}</span>
                    <span className="calculator-btn-text">
                      {language === 'ar' ? 'عرض الحاسبة' : 'View Calculator'}
                    </span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="calculator-btn-icon">
                      <path d="M19 14L12 21L5 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 3V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                )}
              </div>
              
              <div className="carousel-container">
                <button 
                  className="carousel-arrow carousel-arrow-left"
                  onClick={() => {
                    const container = document.querySelector('.menu-grid');
                    scrollCarousel(container, -400);
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                
                <div className="menu-grid">
                  {filteredItems.map(item => {
                    const isSelected = selectedItems.some(b => b.id === item.id);
                    return (
                      <div 
                        key={item.id} 
                        className={`menu-item-card ${isSelected ? 'selected' : ''}`}
                        onClick={() => addItemToBasket(item)}
                      >
                        {isSelected && (
                          <div className="selection-badge">
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                              <circle cx="10" cy="10" r="10" fill="#00C851"/>
                              <path d="M6 10L8.5 12.5L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                        )}
                        {item.image && (
                          <img src={item.image} alt={item.name} className="menu-item-image" />
                        )}
                        <div className="menu-item-content">
                          <h3 className="menu-item-name">{language === 'ar' && item.arabic_name ? item.arabic_name : item.name}</h3>
                          <div className="menu-item-stats">
                            <span>{item.nutrition_facts.energy} {language === 'ar' ? 'سعرة' : 'kcal'}</span>
                            <span>•</span>
                            <span>{item.nutrition_facts.serving}{language === 'ar' ? 'جم' : 'g'}</span>
                          </div>
                        </div>
                        <button
                          className="btn-add"
                          onClick={(e) => {
                            e.stopPropagation();
                            addItemToBasket(item);
                          }}
                        >
                          {isSelected ? (language === 'ar' ? 'إزالة' : 'Remove') : t.add}
                        </button>
                      </div>
                    );
                  })}
                </div>
                
                <button 
                  className="carousel-arrow carousel-arrow-right"
                  onClick={() => {
                    const container = document.querySelector('.menu-grid');
                    scrollCarousel(container, 400);
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* Selected Items Basket */}
            {selectedItems.length > 0 && (
              <div className="basket-section" id="nutrition-calculator">
                <div className="basket-header">
                  <h2 className="section-title">{t.yourSelection} ({selectedItems.length} {t.items})</h2>
                  <button className="btn-remove-all" onClick={removeAllItems}>
                    🗑️ {language === 'ar' ? 'إزالة الكل' : 'Remove All'}
                  </button>
                </div>
                <div className="basket-items">
                  {selectedItems.map(item => (
                    <div key={item.uniqueId} className="basket-item">
                      <span className="basket-item-name">{language === 'ar' && item.arabic_name ? item.arabic_name : item.name}</span>
                      <button
                        className="btn-remove"
                        onClick={() => removeItemFromBasket(item.uniqueId)}
                      >
                        {t.remove}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Nutrition Table */}
            {totalNutrition && (
              <div className="nutrition-section">
                <h2 className="section-title">{t.nutritionFacts}</h2>
                <div className="nutrition-table-card">
                  <div className="nutrition-header">
                    <h3>{t.nutritionCalculator}</h3>
                    <p className="nutrition-subtitle">
                      {t.subtitle}
                    </p>
                  </div>

                  {/* Main Nutrition Values */}
                  <div className="nutrition-main-grid">
                    <div className="nutrition-main-item">
                      <div className="nutrition-value">{Math.round(totalNutrition.energy)} {language === 'ar' ? 'سعرة' : 'kCal'}</div>
                      <div className="nutrition-label">{t.energy}</div>
                    </div>
                    <div className="nutrition-main-item">
                      <div className="nutrition-value">{totalNutrition.total_fat.toFixed(1)}{language === 'ar' ? 'جم' : 'g'}</div>
                      <div className="nutrition-label">{t.totalFat}</div>
                    </div>
                    <div className="nutrition-main-item">
                      <div className="nutrition-value">{totalNutrition.carbs.toFixed(1)}{language === 'ar' ? 'جم' : 'g'}</div>
                      <div className="nutrition-label">{t.carbs}</div>
                    </div>
                    <div className="nutrition-main-item">
                      <div className="nutrition-value">{totalNutrition.protein.toFixed(1)}{language === 'ar' ? 'جم' : 'g'}</div>
                      <div className="nutrition-label">{t.protein}</div>
                    </div>
                  </div>

                  {/* Detailed Nutrition Grid */}
                  <div className="nutrition-detail-grid">
                    <div className="nutrition-row">
                      <div className="nutrition-pair">
                        <span className="nutrition-row-label">{t.serving}:</span>
                        <span className="nutrition-row-value">{Math.round(totalNutrition.serving)}{language === 'ar' ? 'جم' : 'g'}</span>
                      </div>
                      <div className="nutrition-pair">
                        <span className="nutrition-row-label">{t.saturatedFats}:</span>
                        <span className="nutrition-row-value">{totalNutrition.saturated_fats.toFixed(2)}{language === 'ar' ? 'جم' : 'g'}</span>
                      </div>
                      <div className="nutrition-pair">
                        <span className="nutrition-row-label">{t.transFats}:</span>
                        <span className="nutrition-row-value">{totalNutrition.trans_fats.toFixed(2)}{language === 'ar' ? 'جم' : 'g'}</span>
                      </div>
                    </div>
                    <div className="nutrition-row">
                      <div className="nutrition-pair">
                        <span className="nutrition-row-label">{t.cholesterol}:</span>
                        <span className="nutrition-row-value">{totalNutrition.cholesterol.toFixed(1)}{language === 'ar' ? 'ملجم' : 'mg'}</span>
                      </div>
                      <div className="nutrition-pair">
                        <span className="nutrition-row-label">{t.sodium}:</span>
                        <span className="nutrition-row-value">{totalNutrition.sodium.toFixed(1)}{language === 'ar' ? 'ملجم' : 'mg'}</span>
                      </div>
                      <div className="nutrition-pair">
                        <span className="nutrition-row-label">{t.dietaryFibers}:</span>
                        <span className="nutrition-row-value">{totalNutrition.dietary_fibers.toFixed(1)}{language === 'ar' ? 'جم' : 'g'}</span>
                      </div>
                    </div>
                    <div className="nutrition-row">
                      <div className="nutrition-pair">
                        <span className="nutrition-row-label">{t.totalSugar}:</span>
                        <span className="nutrition-row-value">{totalNutrition.total_sugar.toFixed(1)}{language === 'ar' ? 'جم' : 'g'}</span>
                      </div>
                      <div className="nutrition-pair">
                        <span className="nutrition-row-label">{t.addedSugar}:</span>
                        <span className="nutrition-row-value">{totalNutrition.added_sugar.toFixed(1)}{language === 'ar' ? 'جم' : 'g'}</span>
                      </div>
                      <div className="nutrition-pair">
                        <span className="nutrition-row-label">{t.physicalActivity}:</span>
                        <span className="nutrition-row-value">{Math.round(totalNutrition.physical_activity)} {t.minutes}</span>
                      </div>
                    </div>
                  </div>

                  {/* Notes Section */}
                  {notes.length > 0 && (
                    <div className="nutrition-notes">
                      {notes.map(note => (
                        <p key={note.id} className="nutrition-note">
                          {language === 'ar' ? note.text_ar : note.text_en}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
  );
}

// Admin Page Component
function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [adminCategories, setAdminCategories] = useState([]);
  const [adminItems, setAdminItems] = useState([]);
  const [adminNotes, setAdminNotes] = useState([]);

  useEffect(() => {
    checkAuthentication();
  }, []);

  const checkAuthentication = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsLoading(false);
      setIsAuthenticated(false);
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/verify`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setIsAuthenticated(true);
        fetchAdminCategories();
        fetchAdminItems();
        fetchAdminNotes();
      } else {
        localStorage.removeItem('token');
        localStorage.removeItem('username');
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Authentication error:', error);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    fetchAdminCategories();
    fetchAdminItems();
    fetchAdminNotes();
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    setIsAuthenticated(false);
  };

  const fetchAdminCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/categories`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAdminCategories(data);
      }
    } catch (error) {
      console.error('Error fetching admin categories:', error);
    }
  };

  const fetchAdminItems = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/items`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAdminItems(data);
      }
    } catch (error) {
      console.error('Error fetching admin items:', error);
    }
  };

  const fetchAdminNotes = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/notes`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAdminNotes(data);
      }
    } catch (error) {
      console.error('Error fetching admin notes:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="App" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="App">
      {/* Navigation */}
      <nav className="nav-header">
        <div className="container">
          <div className="nav-content">
            <h1 className="nav-logo">Herfy Nutrition Calculator - Admin</h1>
            <div className="nav-links">
              <span style={{ marginRight: '16px', color: '#666' }}>
                Welcome, {localStorage.getItem('username')}
              </span>
              <button onClick={handleLogout} className="nav-link" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                Logout
              </button>
              <Link to="/" className="nav-link">
                Back to Calculator
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <AdminPanel
        categories={adminCategories}
        items={adminItems}
        notes={adminNotes}
        onRefresh={() => {
          fetchAdminCategories();
          fetchAdminItems();
          fetchAdminNotes();
        }}
      />
    </div>
  );
}

// Admin Panel Component
function AdminPanel({ categories, items, notes, onRefresh }) {
  const [activeTab, setActiveTab] = useState('categories');
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showItemForm, setShowItemForm] = useState(false);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [editingNote, setEditingNote] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || (import.meta && import.meta.env && import.meta.env.REACT_APP_BACKEND_URL) || '';

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure? This will delete all items in this category.')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/categories/${categoryId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Failed to delete category');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/items/${itemId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('Failed to delete item');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/notes/${noteId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Failed to delete note');
    }
  };

  return (
    <div className="admin-panel">
      <div className="container">
        <div className="admin-header">
          <h1 className="admin-title">Herfy Admin Panel</h1>
          <p className="admin-subtitle">Manage categories, items, and nutrition facts</p>
        </div>

        {/* Admin Tabs */}
        <div className="admin-tabs">
          <button
            className={`admin-tab ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => setActiveTab('categories')}
          >
            Categories
          </button>
          <button
            className={`admin-tab ${activeTab === 'items' ? 'active' : ''}`}
            onClick={() => setActiveTab('items')}
          >
            Menu Items
          </button>
          <button
            className={`admin-tab ${activeTab === 'notes' ? 'active' : ''}`}
            onClick={() => setActiveTab('notes')}
          >
            Notes
          </button>
        </div>

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div className="admin-content">
            <div className="admin-content-header">
              <h2 className="section-title">Herfy Categories</h2>
              <button
                className="btn-primary"
                onClick={() => {
                  setEditingCategory(null);
                  setShowCategoryForm(true);
                }}
              >
                Add Category
              </button>
            </div>

            {showCategoryForm && (
              <CategoryForm
                category={editingCategory}
                onClose={() => {
                  setShowCategoryForm(false);
                  setEditingCategory(null);
                }}
                onSuccess={() => {
                  setShowCategoryForm(false);
                  setEditingCategory(null);
                  onRefresh();
                }}
              />
            )}

            <div className="admin-grid">
              {categories.map(category => (
                <div key={category.id} className="admin-card">
                  {category.image && (
                    <img src={category.image} alt={category.name} className="admin-card-image" />
                  )}
                  <div className="admin-card-content">
                    <h3 className="admin-card-title">{category.name}</h3>
                    <p className="admin-card-subtitle">Order: {category.order}</p>
                  </div>
                  <div className="admin-card-actions">
                    <button
                      className="btn-secondary btn-sm"
                      onClick={() => {
                        setEditingCategory(category);
                        setShowCategoryForm(true);
                      }}
                    >
                      Edit
                    </button>
                    <button
                      className="btn-delete btn-sm"
                      onClick={() => handleDeleteCategory(category.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Items Tab */}
        {activeTab === 'items' && (
          <div className="admin-content">
            <div className="admin-content-header">
              <h2 className="section-title">Herfy Menu Items</h2>
              <button
                className="btn-primary"
                onClick={() => {
                  setEditingItem(null);
                  setShowItemForm(true);
                }}
              >
                Add Item
              </button>
            </div>

            {/* Bulk Operations Section */}
            <div className="bulk-operations">
              <h3 className="bulk-operations-title">Herfy Bulk Operations</h3>
              <div className="bulk-operations-buttons">
                <button
                  className="btn-secondary"
                  onClick={async () => {
                    try {
                      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/items/template`, {
                        headers: getAuthHeaders()
                      });
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'items_template.csv';
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                    } catch (error) {
                      alert('Error downloading template: ' + error.message);
                    }
                  }}
                >
                  📥 Download Template
                </button>
                <button
                  className="btn-secondary"
                  onClick={async () => {
                    try {
                      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/items/export`, {
                        headers: getAuthHeaders()
                      });
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'items_export.csv';
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                    } catch (error) {
                      alert('Error exporting items: ' + error.message);
                    }
                  }}
                >
                  📤 Export Items
                </button>
                <label className="btn-secondary file-upload-btn">
                  📁 Upload CSV
                  <input
                    type="file"
                    accept=".csv"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;

                      const formData = new FormData();
                      formData.append('file', file);

                      try {
                        const token = localStorage.getItem('token');
                        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/items/bulk-upload`, {
                          method: 'POST',
                          headers: {
                            'Authorization': token ? `Bearer ${token}` : ''
                          },
                          body: formData,
                        });

                        const result = await response.json();

                        if (response.ok) {
                          let message = `✅ Upload Complete!\n\nAdded: ${result.added}\nUpdated: ${result.updated}`;
                          if (result.errors && result.errors.length > 0) {
                            message += `\n\n⚠️ Errors:\n${result.errors.slice(0, 5).join('\n')}`;
                            if (result.errors.length > 5) {
                              message += `\n... and ${result.errors.length - 5} more errors`;
                            }
                          }
                          alert(message);
                          onRefresh();
                        } else {
                          alert('Upload failed: ' + result.detail);
                        }
                      } catch (error) {
                        alert('Error uploading file: ' + error.message);
                      }

                      // Reset file input
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
            </div>

            {showItemForm && (
              <ItemForm
                item={editingItem}
                categories={categories}
                onClose={() => {
                  setShowItemForm(false);
                  setEditingItem(null);
                }}
                onSuccess={() => {
                  setShowItemForm(false);
                  setEditingItem(null);
                  onRefresh();
                }}
              />
            )}

            <div className="admin-grid">
              {items.map(item => {
                const category = categories.find(c => c.id === item.category_id);
                return (
                  <div key={item.id} className="admin-card">
                    {item.image && (
                      <img src={item.image} alt={item.name} className="admin-card-image" />
                    )}
                    <div className="admin-card-content">
                      <h3 className="admin-card-title">{item.name}</h3>
                      {item.item_code && (
                        <p className="admin-card-code">Code: {item.item_code}</p>
                      )}
                      <p className="admin-card-subtitle">{category?.name || 'Unknown Category'}</p>
                      <div className="admin-card-stats">
                        <span>{item.nutrition_facts.energy} kcal</span>
                        <span>•</span>
                        <span>{item.nutrition_facts.serving}g</span>
                      </div>
                    </div>
                    <div className="admin-card-actions">
                      <button
                        className="btn-secondary btn-sm"
                        onClick={() => {
                          setEditingItem(item);
                          setShowItemForm(true);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn-delete btn-sm"
                        onClick={() => handleDeleteItem(item.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Notes Tab */}
        {activeTab === 'notes' && (
          <div className="admin-content">
            <div className="admin-content-header">
              <h2 className="section-title">Herfy Notes</h2>
              <button
                className="btn-primary"
                onClick={() => {
                  setEditingNote(null);
                  setShowNoteForm(true);
                }}
              >
                Add Note
              </button>
            </div>

            {showNoteForm && (
              <NoteForm
                note={editingNote}
                onClose={() => {
                  setShowNoteForm(false);
                  setEditingNote(null);
                }}
                onSuccess={() => {
                  setShowNoteForm(false);
                  setEditingNote(null);
                  onRefresh();
                }}
              />
            )}

            <div className="admin-grid">
              {notes.map(note => (
                <div key={note.id} className="admin-card admin-card-note">
                  <div className="admin-card-content">
                    <h4 className="admin-card-subtitle">English:</h4>
                    <p className="note-preview">{note.text_en}</p>
                    <h4 className="admin-card-subtitle">Arabic:</h4>
                    <p className="note-preview note-preview-ar">{note.text_ar}</p>
                    <p className="admin-card-subtitle">Order: {note.order}</p>
                  </div>
                  <div className="admin-card-actions">
                    <button
                      className="btn-secondary btn-sm"
                      onClick={() => {
                        setEditingNote(note);
                        setShowNoteForm(true);
                      }}
                    >
                      Edit
                    </button>
                    <button
                      className="btn-delete btn-sm"
                      onClick={() => handleDeleteNote(note.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Category Form Component
function CategoryForm({ category, onClose, onSuccess }) {
  const [name, setName] = useState(category?.name || '');
  const [arabicName, setArabicName] = useState(category?.arabic_name || '');
  const [order, setOrder] = useState(category?.order || 0);
  const [image, setImage] = useState(category?.image || null);
  const [imagePreview, setImagePreview] = useState(category?.image || null);
  const [loading, setLoading] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setImage(base64String);
        setImagePreview(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImage(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = category
        ? `${BACKEND_URL}/api/admin/categories/${category.id}`
        : `${BACKEND_URL}/api/admin/categories`;
      
      const method = category ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify({ name, arabic_name: arabicName, order: parseInt(order), image })
      });

      if (response.ok) {
        onSuccess();
      } else {
        alert('Failed to save category');
      }
    } catch (error) {
      console.error('Error saving category:', error);
      alert('Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-modal">
      <div className="form-card">
        <div className="form-header">
          <h3>{category ? 'Edit Herfy Category' : 'Add Herfy Category'}</h3>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Category Name (English)</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label>Category Name (Arabic - اسم الفئة)</label>
            <input
              type="text"
              value={arabicName}
              onChange={(e) => setArabicName(e.target.value)}
              className="form-input"
              dir="rtl"
              placeholder="اسم الفئة بالعربية"
            />
          </div>
          <div className="form-group">
            <label>Display Order</label>
            <input
              type="number"
              value={order}
              onChange={(e) => setOrder(e.target.value)}
              required
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label>Category Image</label>
            {imagePreview && (
              <div className="image-preview-container">
                <img src={imagePreview} alt="Preview" className="image-preview" />
                <button type="button" className="btn-remove-image" onClick={removeImage}>
                  Remove Image
                </button>
              </div>
            )}
            {!imagePreview && (
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="form-input"
              />
            )}
          </div>
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Item Form Component
function ItemForm({ item, categories, onClose, onSuccess }) {
  const [itemCode, setItemCode] = useState(item?.item_code || '');
  const [name, setName] = useState(item?.name || '');
  const [arabicName, setArabicName] = useState(item?.arabic_name || '');
  const [categoryId, setCategoryId] = useState(item?.category_id || (categories[0]?.id || ''));
  const [image, setImage] = useState(item?.image || null);
  const [imagePreview, setImagePreview] = useState(item?.image || null);
  const [nutritionFacts, setNutritionFacts] = useState(item?.nutrition_facts || {
    energy: 0,
    total_fat: 0,
    carbs: 0,
    protein: 0,
    serving: 0,
    saturated_fats: 0,
    trans_fats: 0,
    cholesterol: 0,
    sodium: 0,
    dietary_fibers: 0,
    total_sugar: 0,
    added_sugar: 0,
    physical_activity: 0
  });
  const [loading, setLoading] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setImage(base64String);
        setImagePreview(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImage(null);
    setImagePreview(null);
  };

  const updateNutritionField = (field, value) => {
    setNutritionFacts(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = item
        ? `${BACKEND_URL}/api/admin/items/${item.id}`
        : `${BACKEND_URL}/api/admin/items`;
      
      const method = item ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify({
          item_code: itemCode || null,
          name,
          arabic_name: arabicName,
          category_id: categoryId,
          nutrition_facts: nutritionFacts,
          image
        })
      });

      if (response.ok) {
        onSuccess();
      } else {
        alert('Failed to save item');
      }
    } catch (error) {
      console.error('Error saving item:', error);
      alert('Failed to save item');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-modal">
      <div className="form-card form-card-large">
        <div className="form-header">
          <h3>{item ? 'Edit Herfy Menu Item' : 'Add Herfy Menu Item'}</h3>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label>Item Name (English)</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Item Name (Arabic - اسم الصنف)</label>
              <input
                type="text"
                value={arabicName}
                onChange={(e) => setArabicName(e.target.value)}
                className="form-input"
                dir="rtl"
                placeholder="اسم الصنف بالعربية"
              />
            </div>
          </div>
          <div className="form-group">
            <label>Item Code (Optional - Unique Identifier)</label>
            <input
              type="text"
              value={itemCode}
              onChange={(e) => setItemCode(e.target.value)}
              className="form-input"
              placeholder="e.g., MEAL001, SAND002"
            />
            <small style={{ color: '#666', fontSize: '12px' }}>
              Alphanumeric code for easier CSV bulk updates (leave empty if not needed)
            </small>
          </div>
          <div className="form-group">
            <label>Category</label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              required
              className="form-input"
            >
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Item Image</label>
            {imagePreview && (
              <div className="image-preview-container">
                <img src={imagePreview} alt="Preview" className="image-preview" />
                <button type="button" className="btn-remove-image" onClick={removeImage}>
                  Remove Image
                </button>
              </div>
            )}
            {!imagePreview && (
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="form-input"
              />
            )}
          </div>

          <h4 className="form-section-title">Herfy Nutrition Facts</h4>
          <div className="form-grid form-grid-3">
            <div className="form-group">
              <label>Energy (kcal)</label>
              <input
                type="number"
                step="0.1"
                value={nutritionFacts.energy}
                onChange={(e) => updateNutritionField('energy', e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Total Fat (g)</label>
              <input
                type="number"
                step="0.1"
                value={nutritionFacts.total_fat}
                onChange={(e) => updateNutritionField('total_fat', e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Carbs (g)</label>
              <input
                type="number"
                step="0.1"
                value={nutritionFacts.carbs}
                onChange={(e) => updateNutritionField('carbs', e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Protein (g)</label>
              <input
                type="number"
                step="0.1"
                value={nutritionFacts.protein}
                onChange={(e) => updateNutritionField('protein', e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Serving (g)</label>
              <input
                type="number"
                step="0.1"
                value={nutritionFacts.serving}
                onChange={(e) => updateNutritionField('serving', e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Saturated Fats (g)</label>
              <input
                type="number"
                step="0.01"
                value={nutritionFacts.saturated_fats}
                onChange={(e) => updateNutritionField('saturated_fats', e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Trans Fats (g)</label>
              <input
                type="number"
                step="0.01"
                value={nutritionFacts.trans_fats}
                onChange={(e) => updateNutritionField('trans_fats', e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Cholesterol (mg)</label>
              <input
                type="number"
                step="0.1"
                value={nutritionFacts.cholesterol}
                onChange={(e) => updateNutritionField('cholesterol', e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Sodium (mg)</label>
              <input
                type="number"
                step="0.1"
                value={nutritionFacts.sodium}
                onChange={(e) => updateNutritionField('sodium', e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Dietary Fibers (g)</label>
              <input
                type="number"
                step="0.1"
                value={nutritionFacts.dietary_fibers}
                onChange={(e) => updateNutritionField('dietary_fibers', e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Total Sugar (g)</label>
              <input
                type="number"
                step="0.1"
                value={nutritionFacts.total_sugar}
                onChange={(e) => updateNutritionField('total_sugar', e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Added Sugar (g)</label>
              <input
                type="number"
                step="0.1"
                value={nutritionFacts.added_sugar}
                onChange={(e) => updateNutritionField('added_sugar', e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Physical Activity (min)</label>
              <input
                type="number"
                step="1"
                value={nutritionFacts.physical_activity}
                onChange={(e) => updateNutritionField('physical_activity', e.target.value)}
                className="form-input"
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Note Form Component
function NoteForm({ note, onClose, onSuccess }) {
  const [textEn, setTextEn] = useState(note?.text_en || '');
  const [textAr, setTextAr] = useState(note?.text_ar || '');
  const [order, setOrder] = useState(note?.order || 0);
  const [loading, setLoading] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = note
        ? `${BACKEND_URL}/api/admin/notes/${note.id}`
        : `${BACKEND_URL}/api/admin/notes`;
      
      const method = note ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify({ text_en: textEn, text_ar: textAr, order: parseInt(order) })
      });

      if (response.ok) {
        onSuccess();
      } else {
        alert('Failed to save note');
      }
    } catch (error) {
      console.error('Error saving note:', error);
      alert('Failed to save note');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-modal">
      <div className="form-card">
        <div className="form-header">
          <h3>{note ? 'Edit Herfy Note' : 'Add Herfy Note'}</h3>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Note Text (English)</label>
            <textarea
              value={textEn}
              onChange={(e) => setTextEn(e.target.value)}
              required
              className="form-input form-textarea"
              rows="4"
              placeholder="Enter note in English"
            />
          </div>
          <div className="form-group">
            <label>Note Text (Arabic - ملاحظة)</label>
            <textarea
              value={textAr}
              onChange={(e) => setTextAr(e.target.value)}
              required
              className="form-input form-textarea"
              rows="4"
              dir="rtl"
              placeholder="أدخل الملاحظة بالعربية"
            />
          </div>
          <div className="form-group">
            <label>Display Order</label>
            <input
              type="number"
              value={order}
              onChange={(e) => setOrder(e.target.value)}
              required
              className="form-input"
            />
          </div>
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Note'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default App;
